<template>
  <div class="content fade-in">
    <!-- ✅ Loader Overlay -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>{{ loadingText }}</p>
      </div>
    </transition>

    <!-- ✅ ROW 1: KPI / Summary Counts -->
    <div class="kpi-summary">
      <div class="kpi-item" v-for="card in kpiCards" :key="card.title">
        <div class="kpi-info">
          <h4>{{ card.title }}</h4>
          <div class="value">{{ card.value }}</div>
        </div>
        <i :class="card.icon + ' kpi-icon'"></i>
      </div>
    </div>

    <!-- ✅ ROW 2: Search + Filter -->
    <div class="filter-row">
      <div class="search-box">
        <i class="bx bx-search"></i>
        <input
          type="text"
          placeholder="Search users by name, email, or plan..."
          v-model="searchQuery"
        />
      </div>

      <select v-model="filterStatus" class="select-theme">
        <option value="">All Status</option>
        <option value="Active">Active</option>
        <option value="Deactive">Deactive</option>
      </select>
    </div>

    <!-- ✅ ROW 3: User Cards -->
    <div v-if="!loading && users.length > 0" class="user-grid">
      <div
        class="user-card fade-in"
        v-for="user in users"
        :key="user.id"
      >
        <!-- Header -->
        <div class="user-card-header">
          <div class="user-info">
            <img :src="user.avatar" alt="user avatar" />
            <div>
              <h4>{{ user.name }}</h4>
              <p>{{ user.email }}</p>
            </div>
          </div>

          <!-- ⋮ Dropdown -->
          <div class="menu-wrapper" @click.stop="toggleMenu(user.id)">
            <i class="bx bx-dots-horizontal-rounded"></i>
            <transition name="fade-slide">
              <div
                v-if="openMenu === user.id"
                class="dropdown-menu"
              >
                <button @click="blockUser(user)">
                  <i class="bx bx-block"></i> Block / Unblock
                </button>
                <button @click="updateMembership(user)">
                  <i class="bx bx-refresh"></i> Membership
                </button>
                <button class="danger" @click="deleteUser(user)">
                  <i class="bx bx-trash"></i> Delete User
                </button>
              </div>
            </transition>
          </div>
        </div>

        <!-- Membership -->
        <div class="membership">
          <span class="plan">{{ user.plan }}</span>
          <small>{{ user.createdAt }}</small>
        </div>

        <!-- Stats -->
        <div class="user-stats-grid">
          <div class="stat-item">
            <i class="bx bx-globe"></i>
            <span>Pages</span>
            <strong>{{ user.websites }}</strong>
          </div>
          <div class="stat-item">
            <i class="bx bx-receipt"></i>
            <span>Invoices</span>
            <strong>{{ user.invoices }}</strong>
          </div>
          <div class="stat-item">
            <i class="bx bx-calendar"></i>
            <span>Bookings</span>
            <strong>{{ user.bookings }}</strong>
          </div>
          <div class="stat-item">
            <i class="bx bx-wallet"></i>
            <span>Payments</span>
            <strong>{{ user.payments }}</strong>
          </div>
        </div>

        <!-- Footer -->
        <div class="user-footer">
          <span
            class="status"
            :class="user.status.toLowerCase()"
          >
            {{ user.status }}
          </span>
          <button class="btn-primary" @click="viewUser(user)">
            <i class="bx bx-show"></i> View Details
          </button>
        </div>
      </div>
    </div>

    <!-- 🚫 Empty State -->
    <div v-else-if="!loading && users.length === 0" class="empty-state">
      <i class="bx bx-user-x"></i>
      <p>No users found</p>
      <small>Try adjusting filters or add new users.</small>
    </div>

    <!-- ✅ ROW 4: Pagination -->
    <div
      v-if="!loading && users.length > 0"
      class="pagination-row"
    >
      <span class="pagination-info">
        Showing {{ startIndex + 1 }}–{{ endIndex }} of
        {{ total }} users
      </span>

      <div class="pagination-buttons">
        <button
          :disabled="currentPage === 1"
          @click="prevPage"
        >
          <i class="bx bx-chevron-left"></i>
        </button>
        <button
          v-for="page in totalPages"
          :key="page"
          @click="goToPage(page)"
          :class="{ active: currentPage === page }"
        >
          {{ page }}
        </button>
        <button
          :disabled="currentPage === totalPages"
          @click="nextPage"
        >
          <i class="bx bx-chevron-right"></i>
        </button>
      </div>
    </div>

    <br /><br />
  </div>
</template>

<script>
import {
  getUsersList,
  toggleUserStatus,
  deleteUser,
} from "@/apis/aupadminapis";
import { useToast } from "vue-toastification";

export default {
  name: "UsersList",
  data() {
    return {
      searchQuery: "",
      filterStatus: "",
      currentPage: 1,
      perPage: 6,
      openMenu: null,
      loading: false,
      toast: useToast(),
      kpiCards: [
        { title: "Total Users", value: "--", icon: "bx bx-user" },
        { title: "Free Users", value: "--", icon: "bx bx-user-circle" },
        { title: "Paid Users", value: "--", icon: "bx bx-user-check" },
      ],
      users: [],
      total:0,
    };
  },

  computed: {
    filteredByStatus() {
      return this.filterStatus
        ? this.users.filter((u) => u.status === this.filterStatus)
        : this.users;
    },
    totalPages() {
      return Math.ceil(this.total / this.perPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.perPage, this.total);
    },
  },

  async mounted() {
    await this.loadUsers();
  },

  methods: {
    /** 🔹 Load users from backend */
    async loadUsers() {
      try {
        this.loading = true;
        const res = await getUsersList(this.currentPage-1,"user"); // page_no = 0 (fetch all)
        if (res?.status && Array.isArray(res.records)) {
          this.total = res.total;
          this.users = res.records.map((u) => ({
            id: u.user_id,
            name: `${u.fname || ""} ${u.lname || ""}`.trim() || "Unknown",
            email: u.email || "No email",
            mobile: u.mobile || "--",
            plan: u.user_plan_name || "Free Plan",
            createdAt: `Joined: ${u.created_at ? new Date(u.created_at).toLocaleDateString() : "--"}`,
            websites: u.websites || 0,
            invoices: u.invoices || 0,
            bookings: u.bookings || 0,
            payments: u.payments || 0,
            status: u.status === "1" ? "Active" : "Deactive",
            avatar: u.avatar || "images/uplace.jpg",
          }));

          // 🔹 Update KPIs
          this.kpiCards[0].value = res.total || "0";
          this.kpiCards[1].value = res.free || "0";
          this.kpiCards[2].value = res.paid || "0";
        } else {
          this.users = [];
          this.toast.warning("No users found.");
        }
      } catch (err) {
        console.error("loadUsers error:", err);
        this.toast.error("Failed to load users.");
      } finally {
        this.loading = false;
      }
    },

    /** 🔹 Toggle user menu */
    toggleMenu(id) {
      this.openMenu = this.openMenu === id ? null : id;
    },

    /** 🔹 Block or Unblock User */
    async blockUser(user) {
      try {
        this.loading = true;
        const res = await toggleUserStatus(user.id);
        if (res?.status) {
          this.toast.success(res.success);
          await this.loadUsers();
        } else {
          this.toast.warning(res.success || "Action failed");
        }
      } catch (err) {
        console.error("blockUser error:", err);
        this.toast.error("Failed to update user status.");
      } finally {
        this.loading = false;
      }
    },

    /** 🔹 Delete User */
    async deleteUser(user) {
      if (!confirm(`Are you sure you want to delete ${user.name}?`)) return;
      try {
        this.loading = true;
        const res = await deleteUser(user.id);
        if (res?.status) {
          this.toast.success("User deleted successfully.");
          await this.loadUsers();
        } else {
          this.toast.warning(res.success || "Deletion failed.");
        }
      } catch (err) {
        console.error("deleteUser error:", err);
        this.toast.error("Failed to delete user.");
      } finally {
        this.loading = false;
      }
    },

    /** 🔹 Placeholder for future */
    updateMembership(user) {
      this.toast.success(`Update membership for ${user.name}`);
    },

    /** 🔹 Navigate to user details */
    viewUser(user) {
      sessionStorage.setItem("udt",JSON.stringify(user));
      this.$router.push({ path: "/user_details", query: { id: user.id } });
    },

    /** 🔹 Pagination Controls */
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
      this.loadUsers();
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
      this.loadUsers();
    },
    goToPage(page) {
      this.currentPage = page;
      this.loadUsers();
    },
  },
};
</script>

<style scoped>
/* === Layout === */
.kpi-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.kpi-item {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: var(--transition);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}
.kpi-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
}

.kpi-info h4 {
  color: var(--muted);
  font-size: 13px;
}
.kpi-info .value {
  font-weight: 700;
  font-size: 20px;
}
.kpi-icon {
  color: var(--primary);
  opacity: 0.8;
  font-size: 20px;
}

.filter-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

/* === User Cards Grid === */
.user-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 18px;
}

/* === User Card === */
.user-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: var(--transition);
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}
.user-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 14px rgba(0,0,0,0.08);
}

.user-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.user-info img {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid var(--border);
}
.user-info h4 {
  font-weight: 600;
  font-size: 14px;
}
.user-info p {
  font-size: 12px;
  color: var(--muted);
}

/* Menu */
.menu-wrapper {
  position: relative;
  cursor: pointer;
}
.dropdown-menu {
  position: absolute;
  top: 24px;
  right: 0;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
  display: flex;
  flex-direction: column;
  width: 160px;
  animation: fadeIn 0.25s ease;
}
.dropdown-menu button {
  border: none;
  background: transparent;
  text-align: left;
  padding: 10px 12px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}
.dropdown-menu button:hover {
  background: #f3f4f6;
}
.dropdown-menu .danger {
  color: #dc2626;
}

/* Membership */
.membership {
  margin: 12px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.membership .plan {
  font-size: 13px;
  font-weight: 600;
  color: var(--primary);
}
.membership small {
  font-size: 12px;
  color: var(--muted);
}

/* Stats Grid */
.user-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 12px;
}
.stat-item {
  background: #f9fafb;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 10px;
  text-align: center;
  transition: all 0.3s ease;
}
.stat-item:hover {
  background: #f3f4f6;
  transform: translateY(-2px);
}
.stat-item i {
  color: var(--primary);
  font-size: 1rem;
}
.stat-item span {
  display: block;
  font-size: 12px;
  color: var(--muted);
}
.stat-item strong {
  display: block;
  font-weight: 700;
  color: var(--text);
}

/* Footer */
.user-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--border);
  padding-top: 10px;
  margin-top: 10px;
}
.status {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 8px;
  text-transform: capitalize;
}
.status.active {
  background: #dcfce7;
  color: #166534;
}
.status.deactive {
  background: #fee2e2;
  color: #991b1b;
}

/* Fade animations */
.fade-in {
  animation: fadeIn 0.5s ease both;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Responsive */
@media (max-width: 768px) {
  .user-stats-grid {
    grid-template-columns: 1fr 1fr !important;
  }
  .filter-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .btn-primary {
    width: 100%;
  }
}
/* ===============================
   🧭 REMAINING STYLES FOR USERS LIST PAGE
   =============================== */

/* ✅ Search Box */
.search-box {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 10px;
  flex: 1;
  max-width: 360px;
  transition: all 0.25s ease;
}

.search-box i {
  color: var(--muted);
  font-size: 1.1rem;
  margin-right: 6px;
}

.search-box input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 13px;
  color: var(--text);
  background: transparent;
}

.search-box:hover,
.search-box:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.12);
}

/* ✅ Select Dropdown (Filter) */
.select-theme {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--text);
  cursor: pointer;
  transition: all 0.25s ease;
}

.select-theme:hover,
.select-theme:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.12);
}

/* ✅ Primary Button */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.15);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.25);
}

/* ✅ Pagination */
.pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid var(--border);
  flex-wrap: wrap;
  gap: 10px;
}

.pagination-info {
  font-size: 13px;
  color: var(--muted);
}

.pagination-buttons {
  display: flex;
  gap: 6px;
}

.pagination-buttons button {
  background: #f3f4f6;
  color: var(--text);
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pagination-buttons button.active {
  background: var(--primary);
  color: #fff;
  font-weight: 600;
}

.pagination-buttons button:hover:not(.active) {
  background: #e5e7eb;
}

.pagination-buttons button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ✅ Micro Animations */
.fade-in {
  animation: fadeInUp 0.4s ease both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ✅ Hover Lift Utility */
.hover-lift {
  transition: all 0.25s ease;
}
.hover-lift:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
}

/* ✅ Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 8px;
}
::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
::-webkit-scrollbar-track {
  background: transparent;
}

/* ✅ Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--muted);
  font-size: 14px;
  padding: 40px 0;
}
.empty-state i {
  font-size: 36px;
  margin-bottom: 6px;
  opacity: 0.6;
}

/* ✅ Tooltip Utility (Optional for Actions) */
[data-tooltip] {
  position: relative;
  cursor: pointer;
}
[data-tooltip]::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 120%;
  left: 50%;
  transform: translateX(-50%);
  background: #111827;
  color: #fff;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
[data-tooltip]:hover::after {
  opacity: 1;
}

/* ✅ Responsive Polish */
@media (max-width: 1024px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }
  .search-box {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .user-grid {
    grid-template-columns: 1fr !important;
    gap: 16px !important;
  }
  .user-card {
    padding: 14px !important;
  }
  .user-info h4 {
    font-size: 1rem !important;
  }
  .btn-primary {
    width: 100%;
    justify-content: center;
  }
  .pagination-row {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
}

@media (max-width: 480px) {
  .search-box input {
    font-size: 0.9rem;
  }
  .user-card {
    padding: 10px;
  }
  .user-stats-grid {
    grid-template-columns: 1fr 1fr;
  }
  .stat-item span {
    font-size: 0.75rem;
  }
  .stat-item strong {
    font-size: 0.85rem;
  }
}
/* ===============================
   🧩 ADDITIONAL PATCH FOR USERS PAGE
   =============================== */

/* === Loader Overlay === */
.absolute {
  position: absolute;
}
.inset-0 {
  inset: 0;
}
.backdrop-blur-sm {
  backdrop-filter: blur(6px);
}
.loader {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* === Fade transition (for loader & dropdown) === */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* === Empty State (No Data) === */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  text-align: center;
  padding: 60px 0;
  background: #fff;
  border: 1px dashed var(--border);
  border-radius: 12px;
  margin-top: 20px;
  animation: fadeIn 0.4s ease;
}
.empty-state i {
  font-size: 48px;
  color: var(--primary);
  opacity: 0.7;
  margin-bottom: 8px;
}
.empty-state p {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 4px;
}
.empty-state small {
  font-size: 13px;
  color: #9ca3af;
}

/* === Dropdown Animation Polish === */
.dropdown-menu {
  transform-origin: top right;
  animation: dropdownIn 0.25s ease forwards;
}
@keyframes dropdownIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* === Pagination Buttons Enhancement === */
.pagination-buttons button {
  transition: all 0.25s ease;
  border: 1px solid transparent;
}
.pagination-buttons button:hover:not(.active) {
  background: #fff;
  border-color: var(--border);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

/* === Card Hover Glow === */
.user-card:hover {
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
  transform: translateY(-3px);
}

/* === Toast / Notification Area (Optional future use) === */
.toast-message {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: #e0e7ff;
  color: #1e3a8a;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  animation: fadeInDown 0.4s ease;
}
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translate(-50%, -10px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

/* === Smooth Transition for Cards === */
.user-card,
.kpi-item {
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

/* === Mobile Friendly Fixes === */
@media (max-width: 768px) {
  .empty-state {
    padding: 40px 0;
  }
  .dropdown-menu {
    width: 100%;
    right: auto;
    left: 0;
  }
}

</style>
